import type { MapboxAddressAutocompleteOption } from "dsl/molecules/MapboxAddressInput/mapboxAddressCore";
import { Controller, useFormContext } from "react-hook-form";
import {
  MapboxAddressAutocomplete,
  type MapboxAddressAutocompleteProps,
  mapboxAddressOptionFromLocationLabel,
} from "./autocomplete-mapbox";

export type ControlledMapboxAddressAutocompleteProps = Omit<
  MapboxAddressAutocompleteProps,
  "onSelectedOptionChange" | "selectedOption"
> & {
  name: string;
  onAfterSelectedOptionChange?: (
    option: MapboxAddressAutocompleteOption | null,
  ) => void;
  selectedOptionFromFieldValue?: (
    raw: unknown,
  ) => MapboxAddressAutocompleteOption | null;
};

/**
 * React Hook Form bridge around {@link MapboxAddressAutocomplete}. The bound
 * field should hold the same **display string** as zipcode
 * {@link ControlledAlgoliaAutocomplete} on `profile._location` (typically
 * `opt.label`). Use inside `FormProvider`.
 */
export function ControlledMapboxAddressAutocomplete({
  name,
  onAfterSelectedOptionChange,
  selectedOptionFromFieldValue = mapboxAddressOptionFromLocationLabel,
  ...props
}: ControlledMapboxAddressAutocompleteProps) {
  const { control } = useFormContext();

  return (
    <Controller
      control={control}
      name={name}
      render={({
        field: { onBlur, onChange, ref: _ref, value, ...restField },
        fieldState,
      }) => {
        const errorMessage = fieldState?.error?.message;
        const isInvalid = Boolean(errorMessage);
        const selectedOption = selectedOptionFromFieldValue(value);

        return (
          <MapboxAddressAutocomplete
            {...props}
            {...restField}
            errorMessage={errorMessage}
            isInvalid={isInvalid}
            name={name}
            selectedOption={selectedOption}
            onBlur={onBlur}
            onSelectedOptionChange={(opt) => {
              onChange(opt == null ? null : opt.label);
              onAfterSelectedOptionChange?.(opt);
            }}
          />
        );
      }}
    />
  );
}

ControlledMapboxAddressAutocomplete.displayName =
  "Recare.ControlledMapboxAddressAutocomplete";
